import { Table } from "../../utils/local-antd";
import type { TableProps, TableRowSelection } from "../../utils/local-antd";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { SearchUsersUserDto } from "../../model/admin/dto/SearchUsersUserDto";
import { Entry } from "../../model/entry/Entry";
import { GoogleDriveFileDto } from "../../model/integration/dto/google-drive/GoogleDriveFileDto";

import { Flex } from "../Flex";
import { BaseModuleInstance } from "../../model/module/types/module-instance/BaseModuleInstance";
import { BaseTask } from "../../model/task/Task";
import { Spacing } from "../../theme/Spacing";
import useWindowDimensions from "../../hooks/useWindowDimensions";
import { FontSize } from "../Text";

const Container = styled(Flex)`
  flex-direction: column;
  width: 100%;
  overflow: hidden;
`;

interface AtiraTableProps<T extends object>
  extends Omit<TableProps<T>, "dataSource"> {
  data: T[];
  columns: TableProps<T>["columns"];
  loading?: boolean;
  selectedRowKeys?: React.Key[];
  onSelectedRowKeysChange?: ATVoidFunction<React.Key[]>;
  size?: TableProps<T>["size"];
  pagination?: TableProps<T>["pagination"];
  emptyText?: React.ReactNode;
}

const StyledTableWrapper = styled.div<{ isDataEmpty: boolean }>`
  .ant-table-selection-column {
    position: sticky !important;
  }

  .ant-table-column-sorter {
    color: ${({ theme }) => theme.white} !important;
  }

  .ant-table-column-sorter .active {
    color: ${({ theme }) => theme.white}aa !important;
  }

  th.ant-table-cell {
    background-color: ${({ theme }) => theme.darkMain} !important;
    color: ${({ theme }) => theme.white} !important;
  }

  .ant-table-container {
    &::before,
    &::after {
      box-shadow: none !important;
    }
  }

  .ant-table-body {
    scrollbar-color: ${({ theme }) => `${theme.main} ${theme.sub}`};
    scrollbar-width: auto;
    overflow-y: auto !important;
    min-height: 12rem;

    &::-webkit-scrollbar {
      height: 6px;
      width: 6px;
    }

    &::-webkit-scrollbar-track {
      background: ${({ theme }) => theme.sub};
      border-radius: 4px;
    }

    &::-webkit-scrollbar-thumb {
      background: ${({ theme }) => theme.main};
      border-radius: 4px;
    }

    &::-webkit-scrollbar-thumb:hover {
      background: ${({ theme }) => theme.darkMain};
    }
  }

  ${({ isDataEmpty }) =>
    isDataEmpty
      ? `
    .ant-table-cell-fix {
       position: static !important;
    }
  `
      : ""}

  .ant-table-cell {
    height: 2.5rem;
    padding: ${Spacing.xs} ${Spacing.s} !important;

    p {
      color: inherit;
    }
  }

  /* For cells that have phone input */
  .ant-table-cell:has(.iti__country-list) {
    overflow: visible;

    .iti__country-list {
      max-height: 5rem;
      scrollbar-width: thin;

      &::-webkit-scrollbar {
        height: 1px;
        width: 1px;
      }

      &::-webkit-scrollbar-track {
        background: ${({ theme }) => theme.sub};
        border-radius: 4px;
      }

      &::-webkit-scrollbar-thumb {
        background: ${({ theme }) => theme.main};
        border-radius: 4px;
      }

      &::-webkit-scrollbar-thumb:hover {
        background: ${({ theme }) => theme.darkMain};
      }

      .iti__country-name {
        color: #000000 !important;
        font-size: ${FontSize.xs} !important;
      }

      .iti__dial-code {
        font-size: ${FontSize.xs} !important;
      }
    }
  }
`;

export function AtiraTable<
  T extends
    | Entry
    | BaseTask
    | SearchUsersUserDto
    | BaseModuleInstance
    | GoogleDriveFileDto,
>({
  data = [],
  columns,
  loading = false,
  selectedRowKeys,
  pagination,
  onSelectedRowKeysChange,
  emptyText,
  ...props
}: AtiraTableProps<T>) {
  const { t } = useTranslation();
  const { height: windowHeight } = useWindowDimensions();

  const rowSelection: TableRowSelection<T> = {
    selectedRowKeys,
    onChange: (newSelectedRowKeys: React.Key[], selectedRows: T[]) =>
      onSelectedRowKeysChange?.(newSelectedRowKeys),
  };

  const dataSource = Array.isArray(data)
    ? data.map((x, i) => ({
        ...x,
        key: (x as { _id?: string; id?: string })._id ?? (x as any).id,
      }))
    : [];

  return (
    <Container className="atira-table-container">
      <StyledTableWrapper
        className="atira-table-wrapper"
        isDataEmpty={!dataSource.length}
      >
        <Table
          dataSource={dataSource}
          rowSelection={onSelectedRowKeysChange ? rowSelection : undefined}
          columns={columns?.filter((col: any) => col?.data?.enabled !== false)}
          locale={{
            emptyText: emptyText ?? t("entries.no_entries"),
            triggerAsc: t("common.sort.ascending"),
            triggerDesc: t("common.sort.descending"),
            cancelSort: t("common.sort.cancel"),
          }}
          scroll={{
            y: windowHeight >= 500 ? windowHeight - 300 : 400,
            x: "max-content",
          }}
          loading={loading}
          bordered
          pagination={
            pagination
              ? {
                  ...pagination,
                  locale: {
                    items_per_page: t("common.per_page"),
                  },
                }
              : pagination
          }
          {...props}
        />
      </StyledTableWrapper>
    </Container>
  );
}
